import React from "react";
import LegalPage from "../components/LegalPage";

const sections = [
  {
    id: "commitment",
    heading: "Our commitment",
    body: (
      <p>
        MeinHaus is committed to making renovating a home accessible to everyone, including people with
        disabilities. We are continuously improving the user experience for all visitors, and we aim to conform
        to the{" "}
        <a
          href="https://www.w3.org/TR/WCAG21/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#1AA0DD] hover:text-[#0E7AAF] font-semibold"
        >
          Web Content Accessibility Guidelines (WCAG) 2.1 Level AA
        </a>
        . We also comply with the Accessibility for Ontarians with Disabilities Act (AODA) and the Accessible
        Canada Act.
      </p>
    ),
  },
  {
    id: "what-we-do",
    heading: "What we do",
    body: (
      <>
        <p>To support an accessible experience, MeinHaus:</p>
        <ul className="list-disc pl-5 space-y-2 marker:text-[#1AA0DD]">
          <li>uses semantic HTML and ARIA landmarks so screen readers can navigate the site;</li>
          <li>maintains a minimum 4.5:1 colour-contrast ratio for all body text;</li>
          <li>provides descriptive alt text for meaningful images;</li>
          <li>ensures every interactive element is reachable and operable by keyboard;</li>
          <li>supports visible focus indicators on links, buttons, and form fields;</li>
          <li>captions or transcribes video content used to explain the service;</li>
          <li>respects the user&rsquo;s <code className="text-[13px] font-semibold bg-slate-100 px-1.5 py-0.5 rounded">prefers-reduced-motion</code> system setting.</li>
        </ul>
      </>
    ),
  },
  {
    id: "ongoing-work",
    heading: "Ongoing work",
    body: (
      <p>
        Accessibility is never &ldquo;done.&rdquo; We audit the site at least quarterly with automated tools
        and conduct manual screen-reader and keyboard-only testing on each major release. Known issues are
        triaged into our roadmap with target resolution dates.
      </p>
    ),
  },
  {
    id: "alternatives",
    heading: "Alternative formats",
    body: (
      <>
        <p>
          If you need any of our documents (Privacy Policy, Terms of Service, Guarantee Terms, quotes, invoices)
          in an alternative accessible format &mdash; large print, plain-text, Braille, or audio &mdash;
          email{" "}
          <a
            href="mailto:accessibility@meinhaus.ca"
            className="text-[#1AA0DD] hover:text-[#0E7AAF] font-semibold"
          >
            accessibility@meinhaus.ca
          </a>{" "}
          and we will provide it at no cost within 15 business days.
        </p>
      </>
    ),
  },
  {
    id: "feedback",
    heading: "Feedback &amp; reporting barriers",
    body: (
      <>
        <p>
          If you encounter a barrier on meinhaus.ca, please let us know. We treat every report as a priority
          bug.
        </p>
        <ul className="list-disc pl-5 space-y-2 marker:text-[#1AA0DD]">
          <li>
            <strong>Email:</strong>{" "}
            <a
              href="mailto:accessibility@meinhaus.ca"
              className="text-[#1AA0DD] hover:text-[#0E7AAF] font-semibold"
            >
              accessibility@meinhaus.ca
            </a>
          </li>
          <li>
            <strong>Phone:</strong>{" "}
            <a
              href="tel:+14165551234"
              className="text-[#1AA0DD] hover:text-[#0E7AAF] font-semibold"
            >
              +1 (416) 555-1234
            </a>{" "}
            (TTY available on request)
          </li>
          <li>
            <strong>Mail:</strong> Accessibility, MeinHaus Inc., 100 King Street West, Toronto, ON, Canada.
          </li>
        </ul>
        <p>
          Please include the page URL, the assistive technology you were using (if any), and a description of
          what went wrong. We acknowledge feedback within 5 business days.
        </p>
      </>
    ),
  },
  {
    id: "compliance",
    heading: "Compliance documents",
    body: (
      <p>
        Our AODA Multi-Year Accessibility Plan and most recent Accessibility Compliance Report are available
        on request at{" "}
        <a
          href="mailto:accessibility@meinhaus.ca"
          className="text-[#1AA0DD] hover:text-[#0E7AAF] font-semibold"
        >
          accessibility@meinhaus.ca
        </a>
        .
      </p>
    ),
  },
];

const Accessibility = () => (
  <LegalPage
    eyebrow="Accessibility"
    title="Accessibility Statement"
    lede="Renovating shouldn't require a magnifying glass. Here's how we're making meinhaus.ca usable for everyone."
    updated="February 8, 2026"
    sections={sections}
  />
);

export default Accessibility;
